/**
 * TriggerContext - Coordinates agent triggers and scheduling via Web Worker
 *
 * Sprint 11a Refactor: Moves execution to `agent.worker.ts`
 * Sprint 15: Bidirectional mission flow — worker posts missions back to main thread
 *             Missions are persisted to LangGraph Store (not just React state)
 *
 * Responsibilities:
 * 1. Initialize Agent Worker
 * 2. Proxy user requests to worker
 * 3. Expose worker stats and status
 * 4. Receive mission cards from worker and persist to Store
 * 5. Track agent execution progress
 */

import { createContext, useContext, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { MissionCard } from '@ownyou/shared-types';
import type { TriggerEngineStats, TriggerResult } from '@ownyou/triggers';
import { useAgentWorker } from '../hooks/useAgentWorker';
import { useStore } from './StoreContext';
import { useAuth } from './AuthContext';
import { useIkigai } from './IkigaiContext';
import type { AgentExecutionProgress, ExecutionResult, WorkerInboundMessage, WorkerOutboundMessage } from '../workers/worker-protocol';
import {
  createMissionSpineRunId,
  recordMissionSpineMarker,
} from '../lib/mission-spine/instrumentation';
import { persistWorkerMissions } from '../lib/missions/persistence';

interface TriggerContextValue {
  /** Handle a user request (NL intent routing) */
  handleUserRequest: (request: string) => Promise<TriggerResult | null>;

  /** Check if trigger engine is running */
  isRunning: boolean;

  /** Start the trigger engine */
  start: () => void;

  /** Stop the trigger engine */
  stop: () => void;

  /** Get stats about trigger activity */
  getStats: () => TriggerEngineStats | null;

  /** Recent mission cards generated by agents */
  recentMissions: MissionCard[];

  /** Whether agents are currently executing */
  isExecuting: boolean;

  /** Granular execution progress (which agents are running/complete) */
  executionProgress: AgentExecutionProgress | null;

  /** Result of the last execution batch */
  lastExecutionResult: ExecutionResult | null;

  /** Sprint 17e: Trigger full refresh (pipeline + agents) */
  refreshPipeline: () => Promise<void>;

  /** Sprint 17g-4: Post raw messages to the shared worker (e.g. GENERATE_SUMMARY) */
  postRawMessage: (msg: WorkerInboundMessage) => Promise<WorkerOutboundMessage>;

  /** Get raw worker reference for direct addEventListener (useMissionRefiner) */
  getWorker: () => Worker | null;
}

const TriggerContext = createContext<TriggerContextValue | null>(null);

interface TriggerProviderProps {
  children: ReactNode;
}

export function TriggerProvider({ children }: TriggerProviderProps) {
  // Sprint 17e: Wire pipeline refresh via IkigaiContext
  let ikigaiTriggerRefreshIfStale: (() => Promise<boolean>) | undefined;
  try {
    const ikigai = useIkigai();
    ikigaiTriggerRefreshIfStale = ikigai.triggerRefreshIfStale;
  } catch {
    // IkigaiContext may not be mounted in tests
  }

  const onPipelineRefreshNeeded = useCallback(async () => {
    if (ikigaiTriggerRefreshIfStale) {
      const ran = await ikigaiTriggerRefreshIfStale();
      if (ran) {
        // Notify worker that new data may be available
        window.dispatchEvent(new CustomEvent('ownyou:data-changed', {
          detail: { namespace: 'ownyou.ikigai.opportunities', sourceId: 'pipeline-refresh' },
        }));
      }
    }
  }, [ikigaiTriggerRefreshIfStale]);

  const queryClient = useQueryClient();
  const onMissionsChanged = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['missions'] });
  }, [queryClient]);

  const {
    isRunning,
    stats,
    handleUserRequest: workerHandleRequest,
    start,
    stop,
    isExecuting,
    recentMissions,
    executionProgress,
    lastExecutionResult,
    postRawMessage,
    getWorker,
  } = useAgentWorker({ onPipelineRefreshNeeded, onMissionsChanged });

  const { store, isReady: storeReady } = useStore();
  const { wallet } = useAuth();
  const persistedCountRef = useRef(0);

  // Persist new missions to LangGraph Store when they arrive from the worker.
  // This ensures missions survive page refreshes (useState is volatile).
  useEffect(() => {
    if (!store || !storeReady || !wallet || recentMissions.length === 0) return;
    // Only persist missions we haven't persisted yet
    const newMissions = recentMissions.slice(persistedCountRef.current);
    if (newMissions.length === 0) return;
    console.log(`[TriggerContext] Received ${newMissions.length} new mission(s) from worker, persisting...`);

    const userId = wallet.address;

    (async () => {
      const result = await persistWorkerMissions({
        store,
        userId,
        missions: recentMissions,
        fromIndex: persistedCountRef.current,
      }).catch((err) => {
        console.error('[TriggerContext] Failed to persist worker missions:', err);
        return null;
      });
      if (!result) {
        return;
      }
      persistedCountRef.current = result.persistedTotalCount;
      // Invalidate react-query so useMissions() picks up new data
      queryClient.invalidateQueries({ queryKey: ['missions'] });
      recordMissionSpineMarker({
        runId: createMissionSpineRunId('trigger-persist'),
        hop: 'trigger_persist',
        status: result.persistedCount > 0 ? 'success' : 'skipped',
        namespace: result.namespace[0],
        reasonCode: 'missions-persisted',
        counts: {
          missionCount: result.persistedCount,
          persistedTotalCount: result.persistedTotalCount,
        },
      });
      console.log(`[TriggerContext] Persisted ${result.persistedCount} mission(s) to Store`);
    })();
  }, [recentMissions, store, storeReady, wallet, queryClient]);

  const handleUserRequest = async (request: string): Promise<TriggerResult | null> => {
    try {
      return await workerHandleRequest(request);
    } catch (error) {
      console.error('[TriggerContext] Request failed:', error);
      return null;
    }
  };

  const getStats = () => stats;

  const refreshPipeline = useCallback(async () => {
    await onPipelineRefreshNeeded();
  }, [onPipelineRefreshNeeded]);

  const value: TriggerContextValue = {
    handleUserRequest,
    isRunning,
    start,
    stop,
    getStats,
    recentMissions,
    isExecuting,
    executionProgress,
    lastExecutionResult,
    refreshPipeline,
    postRawMessage,
    getWorker,
  };

  return (
    <TriggerContext.Provider value={value}>
      {children}
    </TriggerContext.Provider>
  );
}

export function useTrigger() {
  const context = useContext(TriggerContext);
  if (!context) {
    throw new Error('useTrigger must be used within a TriggerProvider');
  }
  return context;
}

/**
 * Hook for handling user requests
 */
export function useAgentRequest() {
  const { handleUserRequest, isExecuting } = useTrigger();

  return {
    request: handleUserRequest,
    isLoading: isExecuting,
  };
}
